import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import PermissionErrorPage from "../shared/permissionError";
import ErrorAnalytics from "../../components/analytics/errorAnalytics.jsx";
import StudentAnalytics from "../../components/analytics/studentAnalytics.jsx";
import CourseAnalytics from "../../components/analytics/courseAnalytics.jsx";
import { useCourse } from "../../contexts/CourseContext";
import { useUser } from "../../contexts/UserContext";
import { useAuth } from "../../contexts/AuthContext";
import { userService, analyticsService, questionService, submissionService, courseService, authService } from "../../services/api";
import "../../styles/components/pages/instructor-dashboard.css";

export default function InstructorHome() {
  const [greeting, setGreeting] = useState("");
  const username = localStorage.getItem("usernameData");
  const navigate = useNavigate();
  const { user, roleData } = useUser();
  const { login } = useAuth();
  const { selectedCourse: contextCourse, setCourses: setContextCourses, courses: contextCourses, showJoinModal, openJoinModal, closeJoinModal } = useCourse();
  const [selectedAnalytics, setSelectedAnalytics] = useState("error");
  const [studentUsernames, setStudentUsernames] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [joinError, setJoinError] = useState("");
  const [joinLoading, setJoinLoading] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState("");
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState("");
  const [selectedQuestion, setSelectedQuestion] = useState("");
  const [selectedQuestionID, setSelectedQuestionID] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(true);
  const [stats, setStats] = useState({
    totalSubmissions: "...",
    activeStudents: "...",
    questionSets: "...",
    coursesInProgress: "..."
  });

  // Fetch stats on component mount and when course changes
  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Fetch lighter stats first (no submissions yet)
        const [usernames, topics, coursesInProgress] = await Promise.all([
          userService.getUsernames().catch(() => []),
          questionService.getTopics().catch(() => ({})),
          courseService.getCoursesInProgress().catch(() => [])
        ]);

        // Update stats immediately with what we have
        setStats(prev => ({
          ...prev,
          totalSubmissions: "...", // Keep loading for submissions
          activeStudents: Array.isArray(usernames) ? usernames.length : 0,
          questionSets: Object.keys(topics).length,
          coursesInProgress: Array.isArray(coursesInProgress) ? coursesInProgress.length : 0
        }));
        setStatsLoading(false); // Fast stats are done loading

        // Fetch heavy submissions data separately
        const submissions = await submissionService.getAllSubmissionsByCourse(contextCourse?.CRN).catch(() => []);
        
        setStats(prev => ({
          ...prev,
          totalSubmissions: Array.isArray(submissions) ? submissions.length : 0
        }));
      } catch (error) {
        console.error("Error fetching dashboard stats:", error);
        setStatsLoading(false);
      }
    };

    fetchStats();
  }, [contextCourse]);

  useEffect(() => {
    const fetchStudentUsernames = async () => {
      try {
        const usernames = await userService.getUsernames();
        setStudentUsernames(usernames);
      } catch (error) {
        console.error("Error fetching student usernames:", error);
      }
    };
    fetchStudentUsernames();
  }, []);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        // Use context courses if already loaded, otherwise fetch from API
        if (contextCourses && contextCourses.length > 0) {
          setCourses(contextCourses);
          console.log("Using courses from context:", contextCourses);
        } else if (user?.loginID) {
          // Fetch only courses enrolled by this user
          const coursesData = await courseService.getCoursesForUsername(user.loginID);
          setCourses(coursesData);
          setContextCourses(coursesData); // Also populate CourseContext
          console.log("Fetched user's enrolled courses from API:", coursesData);
        }
      } catch (error) {
        console.error("Error fetching courses:", error);
      }
    };
    fetchCourses();
  }, [user?.loginID, contextCourses, setContextCourses]);

  useEffect(() => {
    const crn = contextCourse?.CRN ?? contextCourse?.id;
    if (crn == null || crn === '') {
      setQuestions([]);
      return;
    }
    const fetchQuestions = async () => {
      try {
        const courseId = contextCourse?.CRN;
        const questionData = await questionService.getProblemsFromCourse(courseId);
        setQuestions(questionData);
      } catch (error) {
        console.error("Error fetching questions for Error Analytics:", error);
      }
    };
    fetchQuestions();
  }, [contextCourse]);

  const fetchStudentIdFromUsername = async (username) => {
    try {
      const studentData = await userService.getStudentIdFromUsername(username);
      return studentData.studentID;
    } catch (error) {
      console.error("Error fetching student ID from username:", error);
    }
  };

  const fetchCourseId = async (courseName) => {
    try {
      const courseData = await analyticsService.getCourseId(courseName);
      return courseData.CRN;
    } catch (error) {
      console.error("Error fetching course ID:", error);
    }
  };

  const handleStudentSelection = async (e) => {
    const studentUsername = e.target.value;
    setSelectedStudent(studentUsername);
    if (studentUsername) {
      setLoading(true);
      const studentId = await fetchStudentIdFromUsername(studentUsername);
      if (studentId) setSelectedStudentId(studentId);
      setLoading(false);
    }
  };

  const handleCourseSelection = async (e) => {
    const courseName = e.target.value;
    setSelectedCourse(courseName);
    if (courseName) {
      setLoading(true);
      try {
        const courseId = await fetchCourseId(courseName);
        if (courseId) setSelectedCourseId(courseId);
        else alert(`Error fetching course ID for ${courseName}`);
      } catch (error) {
        alert(`Error fetching course analytics for ${courseName}`);
      }
      setLoading(false);
    }
  };

  const handleTopicChange = (e) => {
    setSelectedTopic(e.target.value);
    setSelectedQuestion("");
    setSelectedQuestionID(null);
  };

  const handleQuestionChange = (e) => {
    const functionName = e.target.value;
    setSelectedQuestion(functionName);
    const questionID = questions.find((q) => q.functionName === functionName)?.questionID;
    setLoading(true);
    if (questionID) setSelectedQuestionID(questionID);
    else setSelectedQuestionID(null);
    setTimeout(() => setLoading(false), 300);
  };

  const handleTabChange = (tab) => {
    setSelectedAnalytics(tab);
  };

  const renderAnalytics = () => {
    if (loading) {
      return (
        <div className="loading-spinner">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      );
    }
    switch (selectedAnalytics) {
      case "error":
        return (
          <ErrorAnalytics
            courseCrn={contextCourse?.CRN ?? contextCourse?.id}
            selectedTopic={selectedTopic}
            selectedQuestion={selectedQuestion}
            selectedQuestionID={selectedQuestionID}
          />
        );
      case "student":
        return <StudentAnalytics selectedStudentId={selectedStudentId} />;
      case "course":
        return <CourseAnalytics selectedCourseId={selectedCourseId} />;
      default:
        return null;
    }
  };

  const getAnalyticsTitle = () => {
    switch (selectedAnalytics) {
      case "error":
        return "Error Classification Analytics";
      case "student":
        return "Student Performance Analytics";
      case "course":
        return "Course Analytics";
      default:
        return "Analytics Dashboard";
    }
  };

  const handleJoinCourse = async (e) => {
    e.preventDefault();
    setJoinError("");
    setJoinLoading(true);

    try {
      const userId = user?.gtid || user?.loginID;
      if (!userId) {
        setJoinError("User ID not found. Please refresh the page.");
        setJoinLoading(false);
        return;
      }
      console.log("Attempting to join course with code:", joinCode, "for user ID:", userId);
      // Pass role 'instructor' — backend will dual-enroll as both instructor and student
      const response = await courseService.joinByCode({ code: joinCode, gtid: userId, role: 'instructor' });

      // If we get here without an error, the join was successful
      setJoinCode("");
      
      // Close modal after successful join
      closeJoinModal();
      
      // Log out and log back in to refresh user role and courses
      await authService.logout();
      
      // Wait a bit before logging back in
      setTimeout(async () => {
        try {
          await login();
          // After logging back in, refresh courses
          if (user?.loginID) {
            const updatedCourses = await courseService.getCoursesForUsername(user.loginID);
            setContextCourses(updatedCourses);
          }
        } catch (error) {
          console.error("Error during re-authentication:", error);
          setJoinError("Failed to refresh courses. Please refresh the page.");
        }
      }, 500);
    } catch (error) {
      console.error("Error joining course:", error);
      let msg = "An error occurred while joining the course";
      if (error && error.message) {
        if (error.message.includes("404")) {
          msg = "Invalid course code. Please check and try again.";
        } else {
          const parts = error.message.split(":");
          msg = parts.length > 1 ? parts[parts.length - 1].trim() : error.message;
        }
      }
      setJoinError(msg);
    } finally {
      setJoinLoading(false);
    }
  };

  useEffect(() => {
    const currentHour = new Date().getHours();
    if (currentHour < 12) setGreeting("Good morning");
    else if (currentHour < 18) setGreeting("Good afternoon");
    else setGreeting("Good evening");
  }, []);

  // Guard: only instructors may access this page
  if (roleData && roleData !== 'instructor') {
    return <PermissionErrorPage />;
  }

  return (
    <div className="instructor-dashboard">
      
        <>
          {/* Sub-header */}
          <div className="instructor-subheader">
            <div className="instructor-subheader-content">
              <div className="instructor-subheader-top">
                <h2 className="instructor-subheader-greeting">{greeting}, {user?.name || username || 'Instructor'}</h2>
                <div className="instructor-subheader-actions">
                  <Link to="/instructor/manageQuestions" className="instructor-action-btn instructor-action-btn-primary">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                    </svg>
                    Add/Manage Questions
                  </Link>
                  <Link to="/submissionTable" className="instructor-action-btn instructor-action-btn-secondary">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                    View Submissions
                  </Link>
                  <Button 
                    className="instructor-action-btn instructor-action-btn-secondary"
                    onClick={openJoinModal}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="5" x2="12" y2="19"/>
                      <line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                    Join a Course
                  </Button>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="instructor-stats-grid">
                <div className="instructor-stat-card">
                  <div className="instructor-stat-value instructor-stat-primary">
                    {stats.totalSubmissions}
                  </div>
                  <p className="instructor-stat-label">Total Submissions</p>
                </div>
                <div className="instructor-stat-card">
                  <div className="instructor-stat-value instructor-stat-accent">
                    {stats.activeStudents}
                  </div>
                  <p className="instructor-stat-label">Active Students</p>
                </div>
                <div className="instructor-stat-card">
                  <div className="instructor-stat-value instructor-stat-success">
                    {stats.questionSets}
                  </div>
                  <p className="instructor-stat-label">Question Sets</p>
                </div>
                <div className="instructor-stat-card">
                  <div className="instructor-stat-value instructor-stat-warning">
                    {stats.coursesInProgress}
                  </div>
                  <p className="instructor-stat-label">Courses in Progress</p>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <main className="instructor-main-content">
            <div className="instructor-content-card">
              <div className="instructor-card-header">
                <h3 className="instructor-card-title">Analytics</h3>
              </div>
              <div className="instructor-card-content">
                {/* Segmented Control Tabs */}
                <div className="instructor-segmented-control">
                  <button
                    onClick={() => handleTabChange("error")}
                    className={`instructor-segment-btn ${selectedAnalytics === "error" ? "active" : ""}`}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="20" x2="12" y2="10"/>
                      <line x1="18" y1="20" x2="18" y2="4"/>
                      <line x1="6" y1="20" x2="6" y2="16"/>
                    </svg>
                    Error Analytics
                  </button>
                  <button
                    onClick={() => handleTabChange("student")}
                    className={`instructor-segment-btn ${selectedAnalytics === "student" ? "active" : ""}`}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                      <circle cx="9" cy="7" r="4"/>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                    </svg>
                    Student Analytics
                  </button>
                  <button
                    onClick={() => handleTabChange("course")}
                    className={`instructor-segment-btn ${selectedAnalytics === "course" ? "active" : ""}`}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                    </svg>
                    Course Analytics
                  </button>
                </div>

                {/* Analytics Content */}
                <div className="instructor-analytics-wrapper">
                  {renderAnalytics()}
                </div>
              </div>
            </div>
          </main>

          {/* Join Course Modal */}
          <Modal show={showJoinModal} onHide={closeJoinModal}>
            <Modal.Header closeButton={false}>
              <Modal.Title>Join a Course</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <form onSubmit={handleJoinCourse}>
                <div className="mb-3">
                  <label htmlFor="joinCode" className="form-label">Course Code</label>
                  <input
                    type="text"
                    className="form-control"
                    id="joinCode"
                    value={joinCode}
                    onChange={e => setJoinCode(e.target.value.toUpperCase())}
                    style={{ textTransform: 'uppercase' }}
                    required
                    autoFocus
                  />
                </div>
                {joinError && <div className="text-danger mb-2">{joinError}</div>}
                <Button variant="primary" type="submit" disabled={joinLoading || !joinCode} className="w-100">
                  {joinLoading ? 'Joining...' : 'Join'}
                </Button>
              </form>
            </Modal.Body>
          </Modal>
        </>
    </div>
  );
}